import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { format, startOfDay } from 'date-fns';

interface AddSleepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (date: string, sleepTime: string, awakenTime: string, notes?: string) => void;
  sleepLog?: { 
    id: string; 
    date: Date; 
    sleepTime: Date; 
    awakenTime: Date; 
    notes?: string 
  } | null;
}

export function AddSleepDialog({ open, onOpenChange, onSuccess, sleepLog }: AddSleepDialogProps) {
  const { t, dir } = useLanguage();
  const [sleepDate, setSleepDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [sleepTime, setSleepTime] = useState('22:00');
  const [awakenTime, setAwakenTime] = useState('06:00');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sleepLog) {
      setSleepDate(format(sleepLog.date, "yyyy-MM-dd"));
      setSleepTime(format(sleepLog.sleepTime, "HH:mm"));
      setAwakenTime(format(sleepLog.awakenTime, "HH:mm"));
      setNotes(sleepLog.notes || '');
    } else {
      // Default to yesterday's date for sleep (since sleep is typically logged the next day)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setSleepDate(format(yesterday, "yyyy-MM-dd"));
      setSleepTime('22:00');
      setAwakenTime('06:00');
      setNotes('');
    }
  }, [sleepLog, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      onSuccess(sleepDate, sleepTime, awakenTime, notes);
      handleClose();
    } catch (error) {
      console.error('Error submitting sleep log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!sleepLog) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setSleepDate(format(yesterday, "yyyy-MM-dd"));
      setSleepTime('22:00');
      setAwakenTime('06:00');
      setNotes('');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} dir={dir}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {sleepLog ? t('sleep.editSleepLog') || 'Edit Sleep Log' : t('sleep.addSleepLog') || 'Add Sleep Hours'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sleepDate">{t('sleep.date') || 'Date'}</Label>
            <Input
              id="sleepDate"
              type="date"
              value={sleepDate}
              onChange={(e) => setSleepDate(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sleepTime">{t('sleep.sleepTime') || 'Sleep Time'}</Label>
              <Input
                id="sleepTime"
                type="time"
                value={sleepTime}
                onChange={(e) => setSleepTime(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="awakenTime">{t('sleep.awakenTime') || 'Awaken Time'}</Label>
              <Input
                id="awakenTime"
                type="time"
                value={awakenTime}
                onChange={(e) => setAwakenTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('sleep.notes') || 'Notes (Optional)'}</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('sleep.notesPlaceholder') || 'Add any notes about your sleep...'}
              rows={3}
              dir={dir}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? (t('common.saving') || 'Saving...')
                : (sleepLog ? t('common.save') || 'Save' : t('common.add') || 'Add')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

